import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import cardbackground from '../cardbackground.webp';
import buttonImage from '../buttonimage.webp';
import WorksheetCard from '../WorksheetCard';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { checkReadingLimit, handleDeleteReading } from '../utils/readingUtils';

function Astrology({ goBack, goHome, handleSaveProgress }) {
    const [cards, setCards] = useState([]);
    const [pickerVisible, setPickerVisible] = useState(false);
    const [selectedCards, setSelectedCards] = useState([]);
    const [savedReadings, setSavedReadings] = useState([]);
    const [saving, setSaving] = useState(false);
    const [clickedCardId, setClickedCardId] = useState(null);
    const [shuffledCards, setShuffledCards] = useState([]);

    const retrySupabaseQuery = useCallback(async (queryFn, retries = 3, delay = 1000) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                return await queryFn();
            } catch (error) {
                if (attempt < retries) {
                    console.warn(`Retrying query (attempt ${attempt})...`);
                    await new Promise((resolve) => setTimeout(resolve, delay));
                } else {
                    throw error;
                }
            }
        }
    }, []);


    const fetchCards = useCallback(async () => {
        try {
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('cards')
                    .select('id, title, image_path, descriptions, card_number')
                    .eq('component', 'Astrology')
            );
            if (error) throw error;
            setCards(data);
            setShuffledCards([...data]); // Initial shuffle
        } catch (err) {
            toast.error('Failed to load cards.');
            console.error('Error fetching cards:', err.message);
        }
    }, [retrySupabaseQuery]);

    const fetchSavedReadings = useCallback(async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .select('*')
                    .eq('user_id', user.id)
                    .eq('component', 'Astrology')
                    .order('created_at', { ascending: false })
            );
            if (error) throw error;
            setSavedReadings(data || []);
        } catch (err) {
            console.error('Error fetching saved readings:', err.message);
        }
    }, [retrySupabaseQuery]);

    useEffect(() => {
        fetchCards();
        fetchSavedReadings();
    }, [fetchCards, fetchSavedReadings]);

    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    const handleCardClick = (card) => {
        if (selectedCards.length < 8 && !selectedCards.some(c => c.id === card.id)) {
            setSelectedCards([...selectedCards, card]);
            setClickedCardId(card.id);
            setTimeout(() => setClickedCardId(null), 200);
        }
    };

    const handleSaveReading = async () => {
        if (selectedCards.length !== 8) {
            toast.error('Please select exactly 8 cards to save your reading.');
            return;
        }

        setSaving(true);
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                toast.error('User not logged in. Please log in to save your reading.');
                return;
            }

            const canSave = await checkReadingLimit(user.id, 'Astrology', retrySupabaseQuery, supabase);
            if (!canSave) {
                toast.error('You have reached the limit of 10 saved readings for this picker. Delete a reading to save a new one.');
                setSaving(false);
                return;
            }

            const descriptions = selectedCards.reduce((acc, card, index) => {
                const position = `position${index + 1}`;
                acc[position] = card.descriptions?.Astrology?.[position] || card.descriptions || 'No description available';
                return acc;
            }, {});

            const readingData = {
                user_id: user.id,
                component: 'Astrology',
                cards: selectedCards.map((card) => card.id),
                descriptions,
            };

            const { error: insertError } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .insert(readingData)
            );

            if (insertError) {
                console.error('Supabase insert error:', insertError.message);
                throw insertError;
            }

            toast.success('Reading saved successfully!');
            setSelectedCards([]);
            setPickerVisible(false);
            await fetchSavedReadings();
        } catch (err) {
            toast.error('Failed to save reading. Please try again.');
            console.error('Error saving reading:', err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleLoadReading = (reading) => {
        const loadedCards = cards.filter((card) => reading.cards.includes(card.id));
        setSelectedCards(loadedCards.map(card => ({
            ...card,
            descriptions: card.descriptions || 'No description available'
        })));
        setPickerVisible(false);
    };

    const handleStartReading = () => {
        setSelectedCards([]);
        setPickerVisible(!pickerVisible);
        if (cards.length > 0) {
            setShuffledCards(shuffleArray([...cards]));
        }
    };

    return (
        <div
            style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '16px',
                // Add bottom padding to prevent sticky footer from covering content
                paddingBottom: '90px',
            }}
        >
            <ToastContainer />
            <h2
                style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color: '#4B5563',
                    margin: '32px 0 24px 0',
                    textAlign: 'center',
                }}
            >
                Astrology
            </h2>

            {/* Session Helper Cards Row */}
            <div
                className="astrology-session-helper-row"
                style={{
                    display: 'flex',
                    flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '600px',
                    marginBottom: '16px',
                }}
            >
                {/* Left Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    marginBottom: window.innerWidth <= 600 ? '16px' : 0,
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>• The birth chart shows the energy you are here to experience with and through.</div>
                        <div>• Knowing your chart energy can be very validating.</div>
                        <div>• The chart can be transcended in that we can create balance in all the energy so that we are free to choose.</div>
                        <div>• The chart is an endless source of fascinating data about you!</div>
                        <div>• The chart can give clues about expertise as well as challenges.</div>
                        <div>• Relationship charts can give insight about relating dynamics with other people.</div>
                    </div>
                </div>
                {/* Right Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>1. Have you ever had an astrology reading by a professional?</div>
                        <div>2.  Have you ever felt limited by your astrology?</div>
                        <div>3. Have you considered that the energy you are experiencing is the exact energy you need for your purpose on earth?</div>
                        <div>4. Are you familiar with the symbology of astrology?</div>
                        <div>5. Have you tried an Oracle reading below and added the asteroids into your own chart?</div>
                        <div>6. Can you think of a way that astrology can possibly help you?</div>
                    </div>
                </div>
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    width: '100%',
                    maxWidth: '700px',
                    alignItems: 'center',
                }}
            >
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '600px',
                    marginBottom: '24px',
                }}>
                    <WorksheetCard title="Astrology Worksheet" columnName="astrology" />
                </div>

                <div
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        minHeight: '120px',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        borderRadius: '10px',
                        border: '2px solid #4B5563',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                        padding: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0 auto',
                        textAlign: 'center', // <-- center align
                        flexDirection: 'column', // <-- ensure vertical stack
                    }}
                >
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: '0 0 16px 0',
                            textAlign: 'center',
                        }}
                    >
                        Astrology offers information about the energy you are on earth experiencing life with and through. It is my understanding that this energy is what is being experienced with and through, but it does not limit us. We are able to create balance and zero point in the energy field to allow us to be above the energy and have the ability to choose how to use it.
                    </p>
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: '0 0 16px 0',
                            textAlign: 'center',
                        }}
                    >
                        Our birth chart can offer us insights into our personality, strengths, and challenges. There are also many other charts and ways to glean information from the charts.
                    </p>
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: '0 0 16px 0',
                            textAlign: 'center',
                        }}
                    >
                        I have used Astrology intuitively to help me decipher and name the energy influencing me and my experiences.
                    </p>
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: '0 0 16px 0',
                            textAlign: 'center',
                        }}
                    >
                        The information included here is a basic introduction to the birth chart. There is a steep learning curve with Astrology and I encourage anyone interested to pursue it separately if they are inspired to do.
                    </p>
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: '0',
                            textAlign: 'center',
                        }}
                    >
                        If your interest is to start with some basics that can help give you validation and insights to reflect on, then this is a good base to start with. The tutorial and charts included give you a deeper look than a horoscope or knowing your birth zodiac sign could.
                    </p>
                </div>
                <div
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        minHeight: '120px',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        borderRadius: '10px',
                        border: '2px solid #4B5563',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                        padding: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0 auto',
                        justifyContent: 'center',
                    }}
                >
                    <iframe
                        width="560"
                        height="315"
                        src="https://www.youtube.com/embed/KxXGPl7Dfz0?si=pT0e-AJ_ewqj2m_I"
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        style={{ borderRadius: '10px', maxWidth: '100%', width: '100%', minHeight: '200px' }}
                    />
                </div>
                <div
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        minHeight: '120px',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        borderRadius: '10px',
                        border: '2px solid #4B5563',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        margin: '0 auto',
                        gap: '16px',
                    }}
                >
                    {/* Visually hidden screen reader instructions for PDFs */}
                    <div id="pdf-instructions" style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }} aria-hidden="false">
                        The following section contains embedded PDF charts. Each chart is described and has a download link. If you use a screen reader, you may prefer to download and open the PDF in a compatible reader for best accessibility.
                    </div>
                    <div style={{ width: '100%' }}>
                        <iframe
                            src="https://img1.wsimg.com/blobby/go/44bf4d28-4a89-4233-a1bb-c03abf40701a/JuileMandiTullar.life%20Zodiac%20Symbols%20Chart.pdf"
                            title="Zodiac Symbols Chart PDF. This chart displays the symbols for each zodiac sign. Download link below."
                            aria-describedby="pdf-instructions"
                            style={{ width: '100%', height: '600px', maxWidth: '540px', borderRadius: '8px', marginBottom: '8px', border: 'none' }}
                            allow="autoplay"
                        />
                        <a
                            href="https://img1.wsimg.com/blobby/go/44bf4d28-4a89-4233-a1bb-c03abf40701a/JuileMandiTullar.life%20Zodiac%20Symbols%20Chart.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download Zodiac Symbols Chart PDF"
                            style={{
                                display: 'block',
                                textAlign: 'center',
                                color: '#2563eb',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                marginBottom: '12px',
                            }}
                        >
                            Download Zodiac Symbols Chart PDF
                        </a>
                        <iframe
                            src="https://img1.wsimg.com/blobby/go/44bf4d28-4a89-4233-a1bb-c03abf40701a/JuieMandiTullar.life%20Reel%20Houses%20Chart.pdf"
                            title="Reel Houses Chart Blank PDF. This is a blank chart for recording house placements. Download link below."
                            aria-describedby="pdf-instructions"
                            style={{ width: '100%', height: '600px', maxWidth: '540px', borderRadius: '8px', marginBottom: '8px', border: 'none' }}
                            allow="autoplay"
                        />
                        <a
                            href="https://img1.wsimg.com/blobby/go/44bf4d28-4a89-4233-a1bb-c03abf40701a/JuieMandiTullar.life%20Reel%20Houses%20Chart.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download Reel Houses Chart Blank PDF"
                            style={{
                                display: 'block',
                                textAlign: 'center',
                                color: '#2563eb',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                marginBottom: '12px',
                            }}
                        >
                            Download Reel Houses Chart Blank PDF
                        </a>
                        <iframe
                            src="https://img1.wsimg.com/blobby/go/44bf4d28-4a89-4233-a1bb-c03abf40701a/JulieMandiTullar.life%20Reel%20Houses%20Chart%20Blank.pdf"
                            title="Zodiac Reels Houses Chart to Fill In PDF. This chart can be filled in with your own data. Download link below."
                            aria-describedby="pdf-instructions"
                            style={{ width: '100%', height: '600px', maxWidth: '540px', borderRadius: '8px', marginBottom: '8px', border: 'none' }}
                            allow="autoplay"
                        />
                        <a
                            href="https://img1.wsimg.com/blobby/go/44bf4d28-4a89-4233-a1bb-c03abf40701a/JulieMandiTullar.life%20Reel%20Houses%20Chart%20Blank.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download Zodiac Reels Houses Chart to Fill In PDF"
                            style={{
                                display: 'block',
                                textAlign: 'center',
                                color: '#2563eb',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                marginBottom: '12px',
                            }}
                        >
                            Download Zodiac Reels Houses Chart to Fill In PDF
                        </a>
                    </div>
                </div>
                <div
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        minHeight: '120px',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        borderRadius: '10px',
                        border: '2px solid #4B5563',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                        padding: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0 auto',
                    }}
                >
                    <div style={{ width: '100%' }}>
                        <p
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '20px',
                                fontWeight: 'bold',
                                margin: 0,
                                textAlign: 'center',
                            }}
                        >
                            The Astrology Oracle below is a tool to help you to gain insight into what energetic influences may be present in a situation, relationship, perception, or life experience you are asking about.
                        </p>
                        <p
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '20px',
                                fontWeight: 'bold',
                                margin: 0,
                                textAlign: 'center',
                            }}
                        >
                            This is meant to give you insight into the active energy for your question or moment.
                        </p>
                        <p
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '20px',
                                fontWeight: 'bold',
                                margin: 0,
                                textAlign: 'center',
                            }}
                        >
                            You may want to add these asteroids and celestial bodies to your chart to add further insights. Asteroid numbers are included on the cards and can be added to your chart using the additional objects section on Astro.com.
                        </p>
                        <p
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '20px',
                                fontWeight: 'bold',
                                margin: 0,
                                textAlign: 'center',
                            }}
                        >
                            Remember this is just information to reflect on. This is not meant to scare you. It might help to look at the information through a lens of knowing the information is beneficial for you to consider.
                        </p>
                        <p
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '20px',
                                fontWeight: 'bold',
                                margin: 0,
                                textAlign: 'center',
                            }}
                        >
                            This is not meant to be a predictive tool, but you are responsible for the questions you keep in mind when you ask and the energy that you currently hold, that is being reflected back to you. Sometimes when you ask about others you may still get your own energy as a response.
                        </p>
                    </div>
                </div>
            </div>

            <button
                style={{
                    backgroundImage: `url(${buttonImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    width: 'auto',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    margin: '16px auto',
                }}
                onClick={handleStartReading}
            >
                {pickerVisible ? 'Hide Card Picker' : 'Start Card Reading'}
            </button>

            {pickerVisible && (
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start',
                    maxWidth: window.innerWidth <= 768 ? '100%' : '1200px',
                    margin: '0 auto',
                    padding: '8px 16px',
                    overflowX: 'hidden',
                    minHeight: window.innerWidth <= 768 ? '480px' : 'auto',
                }}>
                    {shuffledCards.reduce((rows, card, index) => {
                        const cardsPerRow = window.innerWidth <= 768 ? 40 : 71;
                        const rowIndex = Math.floor(index / cardsPerRow);
                        if (!rows[rowIndex]) rows[rowIndex] = [];
                        rows[rowIndex].push(card);
                        return rows;
                    }, []).map((row, rowIndex) => (
                        <div
                            key={rowIndex}
                            style={{
                                display: 'flex',
                                flex: '0 0 100%',
                                marginTop: rowIndex === 0 ? 0 : '-10px',
                                overflow: 'hidden',
                            }}
                        >
                            {row.map((card, index) => (
                                <div
                                    key={card.id}
                                    style={{
                                        width: window.innerWidth <= 768 ? '150px' : '120px',
                                        height: window.innerWidth <= 768 ? '150px' : '100px',
                                        cursor: 'pointer',
                                        marginLeft: index === 0 ? 0 : '-100px',
                                        boxShadow: clickedCardId === card.id ? '0 0 10px rgba(0, 255, 0, 0.5)' : '0 2px 4px rgba(0, 0, 0, 0.2)',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        transform: clickedCardId === card.id ? 'scale(1.1)' : 'scale(1)',
                                        zIndex: rowIndex * 71 + index,
                                        backgroundImage: 'url(https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/Astrology/Astrology%20Back00.webp)',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        borderRadius: '8px',
                                    }}
                                    onClick={() => handleCardClick(card)}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}

            {selectedCards.length > 0 && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gridTemplateRows: 'repeat(2, 1fr)',
                    gap: '4px',
                    marginTop: '16px',
                    background: 'white',
                    border: '2px solid black',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    padding: '8px',
                    borderRadius: '8px',
                    maxWidth: '600px',
                    margin: '16px auto',
                    width: '100%',
                }}>
                    {selectedCards.map((card, index) => {
                        const labels = [
                            'Known Influence',
                            'Effecting Emotions',
                            'Others Can See',
                            'Hidden Activity',
                            'Utilize This',
                            'Balance This',
                            'Growth Opportunity',
                            'Hidden Benefit'
                        ];
                        return (
                            <div key={card.id} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.3), 0 -4px 6px rgba(0,0,0,0.3)',
                            }}>
                                <img
                                    src={`https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/${card.image_path}`}
                                    alt={card.title}
                                    style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                                    onError={() => console.error(`Failed to load card image: ${card.image_path}`)}
                                />
                                <p style={{
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    marginTop: '8px',
                                    textAlign: 'center',
                                    wordWrap: 'break-word',
                                    maxWidth: '100px',
                                }}>
                                    {labels[index] || 'No description available'}
                                </p>
                            </div>
                        );
                    })}
                </div>
            )}

            {savedReadings.length > 0 && (
                <div
                    style={{
                        marginTop: '32px',
                        width: '100%',
                        maxWidth: '600px',
                        padding: '8px',
                    }}
                >
                    <h4
                        style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#4B5563',
                            textAlign: 'center',
                            marginBottom: '16px',
                        }}
                    >
                        Saved Readings
                    </h4>
                    {savedReadings.map((reading) => (
                        <div
                            key={reading.id}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px',
                                border: '1px solid black',
                                borderRadius: '8px',
                                marginBottom: '8px',
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '14px',
                                color: '#4B5563',
                                background: '#fff',
                            }}
                        >
                            <span onClick={() => handleLoadReading(reading)}>
                                {new Date(reading.created_at).toLocaleString()}
                            </span>
                            <span
                                style={{
                                    color: 'red',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    marginLeft: '16px',
                                }}
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    await handleDeleteReading(
                                        reading.id,
                                        fetchSavedReadings,
                                        retrySupabaseQuery,
                                        supabase
                                    );
                                }}
                            >
                                -delete
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {selectedCards.length === 8 && (
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: 'auto',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        margin: '16px auto',
                    }}
                    onClick={handleSaveReading}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Reading'}
                </button>
            )}

            <footer
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    backgroundImage: `url(${cardbackground})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px 10px 0 0',
                    boxShadow: '3px 3px 5px rgba(0, 0, 0, 0.3)',
                    padding: '10px',
                    textAlign: 'center',
                    zIndex: 1000,
                    minHeight: '60px',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '10px',
                }}
            >
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={goBack}
                >
                    Back
                </button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={goHome}
                >
                    Home
                </button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={handleSaveProgress}
                >
                    Save
                </button>
            </footer>
        </div>
    );
}

export default Astrology;